class CartDrawerRecommendations extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.fetchRecommendations();
  }

  getCartProductIds() {
    try {
      const ids = JSON.parse(this.dataset.cartProductIds || '[]');
      return [...new Set(ids.map((id) => String(id)))];
    } catch (error) {
      return [];
    }
  }

  fetchRecommendations() {
    const cartProductIds = this.getCartProductIds();

    if (!cartProductIds.length) {
      this.hide();
      return;
    }

    const url = this.dataset.url;
    const sectionId = this.dataset.sectionId;
    const limit = parseInt(this.dataset.limit, 10) || 8;
    // In collection mode the section ignores product_id and always returns
    // the same collection products, so a single request is enough.
    const useCollection = this.dataset.useCollection === 'true';
    const productIdsToQuery = useCollection ? [cartProductIds[0]] : cartProductIds;

    const requests = productIdsToQuery.map((productId) =>
      fetch(`${url}?section_id=${sectionId}&product_id=${productId}&limit=${limit}&intent=related`)
        .then((response) => (response.ok ? response.text() : ''))
        .catch(() => '')
    );

    Promise.all(requests)
      .then((responses) => {
        const excludedIds = new Set(cartProductIds);
        const cardsHtml = [];

        responses.forEach((responseText) => {
          if (!responseText) return;

          const html = document.createElement('div');
          html.innerHTML = responseText;

          html.querySelectorAll('.grid__item').forEach((item) => {
            const productId = item.dataset.productId;
            if (!productId || excludedIds.has(productId) || cardsHtml.length >= limit) return;

            excludedIds.add(productId);
            cardsHtml.push(item.innerHTML);
          });
        });

        if (!cardsHtml.length) {
          this.hide();
          return;
        }

        this.render(cardsHtml);
      })
      .catch((error) => {
        console.error(error);
        this.hide();
      });
  }

  hide() {
    this.classList.add('cart-drawer-recommendations--hidden');
    this.innerHTML = '';
  }

  render(cardsHtml) {
    const perPage = parseInt(this.dataset.perPage, 10) || 2;
    const heading = this.dataset.heading || '';

    const slides = [];
    for (let i = 0; i < cardsHtml.length; i += perPage) {
      slides.push(cardsHtml.slice(i, i + perPage));
    }
    const slideCount = slides.length;

    const caretIcon =
      '<svg aria-hidden="true" focusable="false" class="icon icon-caret" viewBox="0 0 10 6"><path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"/></svg>';

    const slidesMarkup = slides
      .map((slideCards, index) => {
        const ariaAttrs =
          slideCount > 1
            ? `aria-roledescription="slide" aria-label="${index + 1} of ${slideCount}"`
            : '';
        return `
          <div
            id="Slide-CartDrawerRecommendations-${index + 1}"
            class="cart-drawer-recommendations__slide grid__item slider__slide slideshow__slide"
            tabindex="-1"
            role="group"
            ${ariaAttrs}
          >
            <div class="cart-drawer-recommendations__grid" role="list">
              ${slideCards.map((cardHtml) => `<div class="cart-drawer-recommendations__grid-item">${cardHtml}</div>`).join('')}
            </div>
          </div>`;
      })
      .join('');

    const controlsMarkup =
      slideCount > 1
        ? `
        <div class="slider-buttons no-js-hidden">
          <button type="button" class="slider-button slider-button--prev" name="previous" aria-label="Previous slide">
            ${caretIcon}
          </button>
          <div class="slider-counter slider-counter--dots">
            <div class="slideshow__control-wrapper">
              ${slides
                .map(
                  (_, index) => `
                <button
                  class="slider-counter__link slider-counter__link--dots link"
                  aria-label="Go to slide ${index + 1} of ${slideCount}"
                  aria-controls="Slider-CartDrawerRecommendations"
                >
                  <span class="dot"></span>
                </button>`
                )
                .join('')}
            </div>
          </div>
          <button type="button" class="slider-button slider-button--next" name="next" aria-label="Next slide">
            ${caretIcon}
          </button>
        </div>`
        : '';

    this.innerHTML = `
      <div class="cart-drawer-recommendations__inner">
        ${heading ? `<h2 class="cart-drawer-recommendations__heading h4">${heading}</h2>` : ''}
        <slideshow-component class="slider-mobile-gutter">
          <div
            id="Slider-CartDrawerRecommendations"
            class="cart-drawer-recommendations__slider grid grid--1-col slider slider--everywhere"
            role="list"
            ${slideCount > 1 ? 'aria-label="Slider"' : ''}
          >
            ${slidesMarkup}
          </div>
          ${controlsMarkup}
        </slideshow-component>
      </div>
    `;

    this.syncSliderHeightToActiveSlide(slideCount);
  }

  // Slides sit side by side in a single flex row, so by default the row's
  // height stretches to match the tallest slide, leaving empty space under
  // shorter ones. Instead, size the row to just the currently visible slide,
  // and re-measure whenever the slideshow moves to a different slide.
  syncSliderHeightToActiveSlide(slideCount) {
    if (slideCount <= 1) return;

    const slider = this.querySelector('#Slider-CartDrawerRecommendations');
    const slideshow = this.querySelector('slideshow-component');
    if (!slider || !slideshow) return;

    const syncHeight = () => {
      // Don't rely on [aria-hidden], which SlideshowComponent updates in a
      // separate 'scroll' listener that runs *after* the one dispatching
      // 'slideChanged' — reading it here would still show the previous
      // slide, undersizing (and clipping) a taller new slide. currentPage
      // is updated before 'slideChanged' fires, so index off that instead.
      const slides = slider.querySelectorAll('.cart-drawer-recommendations__slide');
      const currentPage = slideshow.currentPage || 1;
      const activeSlide = slides[currentPage - 1] || slides[0];
      if (activeSlide) slider.style.height = `${activeSlide.offsetHeight}px`;
    };

    syncHeight();
    slideshow.addEventListener('slideChanged', syncHeight);
  }
}

customElements.define('cart-drawer-recommendations', CartDrawerRecommendations);
